import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAvailableCities from "@/hooks/useAvailableCities";
import { useAgentSuggestions } from "@/hooks/useAgentDirectory";
import { getAutocompletePredictions, preloadPlaces } from "@/services/placesService";
import { getGeoLocation } from "@/services/commonServices";
import { pageURLs } from "@/constants";
import { DIRECTORY_PATH, slugify, townPath } from "@/components/DirectoryBreadcrumb";
import "./AgentDirectorySearch.scss";

const SUGGEST_DELAY_MS = 200;
const MAX_PLACES = 5;

const componentOf = (result, type, key = "short_name") =>
  (result?.address_components || []).find((part) => part.types?.includes(type))?.[key] || "";

export const locationFromGeocode = (result) => {
  const point = result?.geometry?.location;
  const latitude = typeof point?.lat === "function" ? point.lat() : point?.lat;
  const longitude = typeof point?.lng === "function" ? point.lng() : point?.lng;
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return null;
  return {
    city: componentOf(result, "locality", "long_name") || componentOf(result, "postal_code"),
    state: componentOf(result, "administrative_area_level_1"),
    latitude: Number(latitude),
    longitude: Number(longitude),
  };
};

export const geocodePlace = async (address) => {
  const geo = await getGeoLocation(`address=${encodeURIComponent(address)}&components=country:US|country:PR`);
  return locationFromGeocode(geo?.results?.[0]);
};

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

const PinIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path
      d="M10 18s6-5.3 6-10a6 6 0 1 0-12 0c0 4.7 6 10 6 10Zm0-7.5A2.5 2.5 0 1 0 10 5.5a2.5 2.5 0 0 0 0 5Z"
      fill="currentColor"
    />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <path
      d="M8.5 3a5.5 5.5 0 1 0 3.4 9.8l3.6 3.7 1.1-1.1-3.7-3.6A5.5 5.5 0 0 0 8.5 3Zm0 1.6a3.9 3.9 0 1 1 0 7.8 3.9 3.9 0 0 1 0-7.8Z"
      fill="currentColor"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
    <path d="M4 17V4.5L11 3v14h1V7.5l4 1.2V17h1.5v1.5h-15V17H4Zm2.5-10h2V8.5h-2V7Zm0 3h2v1.5h-2V10Zm0 3h2v1.5h-2V13Z" fill="currentColor" />
  </svg>
);

export const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

export const legacyPath = (location) => {
  const params = new URLSearchParams();
  if (location.city) params.set("city", location.city);
  if (location.state) params.set("state", location.state);
  params.set("lat", String(location.latitude));
  params.set("lng", String(location.longitude));
  return `${DIRECTORY_PATH}?${params.toString()}`;
};

export const useDirectorySearchActions = () => {
  const navigate = useNavigate();
  return {
    onPickPlace: (place) => navigate(townPath(place.stateSlug, place.slug)),
    onPickLocation: (location) => navigate(legacyPath(location)),
    onPickBrokerage: (name) => navigate(`${DIRECTORY_PATH}?${new URLSearchParams({ b: name }).toString()}`),
    onOpenAgent: (agent) => navigate(`${pageURLs.agentInfo}/${agent.slug}`),
  };
};

const AgentDirectorySearch = ({ place, location, onPickPlace, onPickLocation, onSearchText, onPickBrokerage, onOpenAgent }) => {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [places, setPlaces] = useState([]);
  const [active, setActive] = useState(-1);
  const latest = useRef(0);
  const pickToken = useRef(0);
  const wrapRef = useRef(null);
  const { featuredCities, filterCities } = useAvailableCities();
  const query = text.trim();
  const debounced = useDebounced(query, SUGGEST_DELAY_MS);
  const suggestions = useAgentSuggestions(debounced, location).data;

  useEffect(() => {
    preloadPlaces();
  }, []);

  useEffect(() => {
    const requestId = ++latest.current;
    if (debounced.length < 2) {
      setPlaces([]);
      return;
    }
    getAutocompletePredictions(debounced, {
      types: /^\d/.test(debounced) ? ["(regions)"] : ["(cities)"],
      componentRestrictions: { country: ["us", "pr"] },
    }).then((predictions) => {
      if (requestId === latest.current) setPlaces((predictions || []).slice(0, 4));
    });
  }, [debounced]);

  useEffect(() => {
    const close = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const finish = () => {
    setText("");
    setOpen(false);
    setActive(-1);
  };

  // A slow geocode must not overwrite a later pick, and a failed one must not leave the menu stuck open.
  const pickPlace = async (address) => {
    const token = ++pickToken.current;
    const found = await geocodePlace(address).catch(() => null);
    if (token !== pickToken.current) return;
    if (found) onPickLocation(found);
    finish();
  };

  const sections = useMemo(() => {
    if (!query) {
      return [
        {
          title: "Featured markets",
          rows: featuredCities.map((market) => ({
            key: `featured-${market.displayName}`,
            label: market.displayName,
            image: market.image,
            onSelect: () => {
              finish();
              onPickPlace({ stateSlug: String(market.state).toLowerCase(), slug: slugify(market.city) });
            },
          })),
        },
      ];
    }
    const footprint = (suggestions?.places || []).map((item) => ({
      key: `place-${item.stateSlug}-${item.slug}`,
      name: `${item.name}, ${String(item.state || item.stateSlug || "").toUpperCase()}`,
      icon: <PinIcon />,
      onSelect: () => {
        finish();
        onPickPlace({ stateSlug: item.stateSlug, slug: item.slug });
      },
    }));
    const markets = filterCities(query)
      .slice(0, 3)
      .map((market) => ({
        key: `market-${market.displayName || `${market.city}-${market.state}`}`,
        name: market.displayName || `${market.city}, ${market.state}`,
        image: featuredCities.find((featured) => featured.displayName === market.displayName)?.image,
        icon: <PinIcon />,
        onSelect: () => {
          finish();
          onPickPlace({ stateSlug: String(market.state).toLowerCase(), slug: slugify(market.city) });
        },
      }));
    const google = places.map((prediction) => ({
      key: `google-${prediction.place_id}`,
      name: String(prediction.description || "").replace(/, USA$/, ""),
      icon: <PinIcon />,
      onSelect: () => pickPlace(prediction.description),
    }));
    const seen = new Set();
    const ours = [...footprint, ...markets];
    const placeRows = [...ours, ...(ours.length ? [] : google)]
      .filter((row) => {
        const name = row.name.toLowerCase();
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      })
      .slice(0, MAX_PLACES)
      .map(({ name, ...row }) => ({ ...row, label: name }));
    return [
      { title: "Places", rows: placeRows },
      {
        title: "Agents",
        rows: (suggestions?.agents || []).map((agent) => ({
          key: `agent-${agent.slug}`,
          label: agent.name,
          sub: [agent.company, agent.mlsName].filter(Boolean).join(" · "),
          avatar: agent.photo || null,
          initials: initialsOf(agent.name),
          onSelect: () => {
            finish();
            onOpenAgent(agent);
          },
        })),
      },
      {
        title: "Brokerages",
        rows: (suggestions?.brokerages || []).map((brokerage) => ({
          key: `brokerage-${brokerage.name}`,
          label: brokerage.name,
          icon: <BuildingIcon />,
          onSelect: () => {
            finish();
            onPickBrokerage(brokerage.name);
          },
        })),
      },
      {
        title: null,
        rows: onSearchText
          ? [
          {
            key: "search-text",
            label: `Search “${query}”${place ? ` in ${place}` : ""}`,
            icon: <SearchIcon />,
            isRefine: true,
            onSelect: () => {
              finish();
              onSearchText(query);
            },
          },
        ]
          : [],
      },
    ].filter((section) => section.rows.length);
  }, [query, featuredCities, filterCities, places, suggestions, place, onSearchText]);

  const rows = sections.flatMap((section) => section.rows);

  // Late suggestions reshuffle the rows; drop the highlight so Enter can't select a different row than the one shown.
  const rowKeys = rows.map((row) => row.key).join("|");
  useEffect(() => {
    setActive(-1);
  }, [rowKeys]);

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(rows.length - 1, index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(-1, index - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (active >= 0 && rows[active]) rows[active].onSelect();
      else if (query && onSearchText) {
        finish();
        onSearchText(query);
      } else if (rows[0]) rows[0].onSelect();
    } else if (event.key === "Escape") {
      // Chrome clears a type=search field on Escape and fires `input`, which would reopen the menu.
      event.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  };

  let rowIndex = -1;

  return (
    <div className="directory-search" ref={wrapRef}>
      <div className={`directory-search__box${open ? " is-open" : ""}`}>
        <span className="directory-search__icon">
          <SearchIcon />
        </span>
        <input
          type="search"
          role="combobox"
          aria-expanded={open && rows.length > 0}
          aria-controls="directory-search-list"
          aria-activedescendant={open && active >= 0 && rows[active] ? `directory-search-option-${active}` : undefined}
          aria-label="City, ZIP, agent or brokerage"
          autoComplete="off"
          placeholder="City, ZIP, agent or brokerage"
          value={text}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setText(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && rows.length ? (
        <div className="directory-search__menu" id="directory-search-list" role="listbox">
          {sections.map((section) => (
            <div className="directory-search__section" key={section.title || "refine"}>
              {section.title ? <p className="directory-search__heading">{section.title}</p> : null}
              {section.rows.map((row) => {
                rowIndex += 1;
                const index = rowIndex;
                return (
                  <button
                    type="button"
                    role="option"
                    id={`directory-search-option-${index}`}
                    aria-selected={index === active}
                    key={row.key}
                    className={`directory-search__row${index === active ? " is-active" : ""}${row.isRefine ? " is-refine" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActive(index)}
                    onClick={row.onSelect}
                  >
                    <span className="directory-search__thumb">
                      {row.image ? (
                        <img src={row.image} alt="" />
                      ) : row.avatar ? (
                        <img src={row.avatar} alt="" className="is-round" />
                      ) : row.initials ? (
                        <span className="directory-search__initials">{row.initials}</span>
                      ) : (
                        row.icon
                      )}
                    </span>
                    <span className="directory-search__text">
                      <span className="directory-search__label">{row.label}</span>
                      {row.sub ? <span className="directory-search__sub">{row.sub}</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AgentDirectorySearch;
